import React, { ReactNode } from 'react';
import styled, { css, ThemeProps, useTheme } from 'styled-components';
import {
  AbsoluteThemeColorType,
  ThemeColorKeyType,
  ThemeDisplayStyleKeyType,
  ThemeFontKeyType,
  ThemeType,
} from 'styles';

export const DEFAULT_FONT_FAMILY = 'NouvelR';
export const SECONDARY_FONT_FAMILY = 'SF Pro Text';

export const STRONG_FONT_WEIGHT = 600;

type TransformType = 'capitalize' | 'lowercase' | 'uppercase' | 'sentence-case' | 'none';
type Cursor = 'initial' | 'pointer' | 'text';
export type Decoration = 'none' | 'underline' | 'line-through';

export interface TextProps {
  className?: string;
  type: ThemeFontKeyType;
  transform?: TransformType;
  cursor?: Cursor;
  decoration?: Decoration;
  displayStyle?: ThemeDisplayStyleKeyType | ThemeDisplayStyleKeyType[] | false;
  disableGutter?: boolean;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  id?: string;
  ellipsis?: boolean;
  hoverUnderLine?: boolean;
  noWrap?: boolean;
  whiteSpace?: React.CSSProperties['whiteSpace'];
  children?: ReactNode | string;
  'data-cy'?: string;
  color?: ThemeColorKeyType;
  bold?: boolean;
}

type StyledTextProps = Omit<TextProps, 'color' | 'bold'> & {
  $color?: ThemeColorKeyType;
  $bold?: boolean;
};

const fontStyles = css<StyledTextProps>`
  font-family: ${({ theme, type }: ThemeProps<ThemeType> & StyledTextProps) =>
    theme.font[type]?.family ?? DEFAULT_FONT_FAMILY};
  font-size: ${({ theme, type }: ThemeProps<ThemeType> & StyledTextProps) => theme.font[type]?.size + 'px'};
  font-weight: ${({ theme, type, $bold }: ThemeProps<ThemeType> & StyledTextProps) =>
    $bold ? STRONG_FONT_WEIGHT : theme.font[type]?.weight};
  line-height: ${({ theme, type }: any) =>
    theme.font[type] && theme.font[type]?.lineHeight
      ? typeof theme.font[type].lineHeight === 'number'
        ? `${theme.font[type].lineHeight}px`
        : theme.font[type].lineHeight
      : 'inherit'};
  letter-spacing: ${({ theme, type }: any) => theme.font[type] && theme.font[type]?.letterSpacing + 'px'};
  cursor: ${({ cursor }) => cursor ?? 'inherit'};
  color: ${({ theme, type, $color }: ThemeProps<ThemeType> & StyledTextProps) =>
    $color ? theme.color[$color] : theme.font[type]?.color};
  text-transform: ${({ transform }) => transform ?? 'none'};
  text-decoration: ${({ decoration }) => decoration ?? 'none'};
  margin-bottom: ${({ theme, type, disableGutter }) =>
    !disableGutter && theme.font[type]?.heading ? '0.5em' : 'initial'};
  text-overflow: ${({ ellipsis }) => (ellipsis ? 'ellipsis' : 'initial')};
  overflow: ${({ ellipsis }) => (ellipsis ? 'hidden' : 'initial')};
  white-space: ${({ noWrap, whiteSpace }) => {
    if (noWrap) return 'nowrap';
    if (whiteSpace) return whiteSpace;
    return 'inherit';
  }};
  ${({ theme, displayStyle }) =>
    displayStyle &&
    (Array.isArray(displayStyle)
      ? displayStyle.reduce((a, n) => a + theme.displayStyle[n], '')
      : theme.displayStyle[displayStyle])};

  :hover {
    text-decoration: ${({ hoverUnderLine }) => (hoverUnderLine ? 'underline' : 'initial')};
  }

  p {
    margin: 0;
  }
`;

const P = styled.span<StyledTextProps>`
  ${fontStyles}
`;
const H1 = styled.h1<StyledTextProps>`
  ${fontStyles}
`;
const H2 = styled.h2<StyledTextProps>`
  ${fontStyles}
`;
const H3 = styled.h3<StyledTextProps>`
  ${fontStyles}
`;
const H4 = styled.h4<StyledTextProps>`
  ${fontStyles}
`;
const H5 = styled.h5<StyledTextProps>`
  ${fontStyles}
`;
const H6 = styled.h6<StyledTextProps>`
  ${fontStyles}
`;

export const Text = (props: TextProps) => {
  const { font } = useTheme();
  const variant = font[props.type];
  const { color, bold, ...rest } = props;
  // `color` and `bold` are passed as transient ($-prefixed) props so they are consumed by
  // styled-components for styling purposes only and never forwarded to the DOM element.
  const styledProps: StyledTextProps = { ...rest, $color: color, $bold: bold };
  const CustomText = () =>
    props.transform === 'sentence-case' ? <SentenceCase>{props.children}</SentenceCase> : <>{props.children}</>;

  switch (variant?.heading) {
    case 1:
      return (
        <H1 {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </H1>
      );
    case 2:
      return (
        <H2 {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </H2>
      );
    case 3:
      return (
        <H3 {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </H3>
      );
    case 4:
      return (
        <H4 {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </H4>
      );
    case 5:
      return (
        <H5 {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </H5>
      );
    case 6:
      return (
        <H6 {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </H6>
      );
    default:
      return (
        <P {...styledProps} data-cy={props['data-cy']}>
          <CustomText />
        </P>
      );
  }
};

// `Text`'s own `color` prop is a theme color *token key* (`ThemeColorKeyType`), whereas
// `ColoredText` historically accepts an absolute color *value* (`AbsoluteThemeColorType`) under
// the same prop name. Casting away the base `color` prop here avoids a type-level collision
// between the two so `ColoredText`'s existing public API is preserved unchanged.
const ColoredTextBase = (Text as unknown) as React.ComponentType<Omit<TextProps, 'color'>>;

export const ColoredText = styled(ColoredTextBase)<{ color: AbsoluteThemeColorType }>`
  color: ${({ color }) => color};
`;

// do we want to handle also array?
const toLowercase = (text: ReactNode) => {
  if (typeof text === 'string') {
    return text.toLowerCase();
  } else {
    return text;
  }
};

export const Lowercase = ({ children }: { children: ReactNode }) => {
  return <>{toLowercase(children)}</>;
};

// Transforms only pure strings, multiple elements needs to be converted separately
export const SentenceCase = ({ children }: { children: string | ReactNode }) => {
  if (!children || children !== 'string') {
    return <>{sentenceCase(children as string)}</>;
  }
  return <>{children}</>;
};

export const capitalize = (text: string) =>
  text && text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export const sentenceCase = (text: string) =>
  text && text.length > 0 ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;

export const Label = styled.div<{
  width?: number;
  height?: number;
}>`
  display: inline-block;
  height: ${({ height }) => (height ? height + 'px' : 'auto')};
  width: ${({ width }) => (width ?? 200) + 'px'};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin: auto 0 auto 0;
`;
